import { computed } from 'vue';
import { getPagePermissions } from '@/constants/permissions';

/**
 * 權限管理 composable
 * 提供頁面級別的權限控制功能
 *
 * @param {string} pageKey - 頁面識別鍵，對應 pagePermissionMap 中的鍵值
 * @returns {Object} 包含權限檢查方法的物件
 */
export function usePermission(pageKey) {
    // 檢查 pageKey 是否有效
    if (!pageKey || typeof pageKey !== 'string') {
        console.error('usePermission: pageKey 參數無效:', pageKey);
        // 返回安全的預設值
        return {
            canView: computed(() => false),
            canCreate: computed(() => false),
            canUpdate: computed(() => false),
            canDelete: computed(() => false),
            canCopy: computed(() => false),
            canGenerate: computed(() => false),
            canFinish: computed(() => false),
            canBatchUpload: computed(() => false),
            canBatchDownload: computed(() => false),
            canExport: computed(() => false),
            canAssignRole: computed(() => false),
            canOperate: computed(() => false),
            availablePermissions: computed(() => ({})),
            userPermissionIds: computed(() => []),
            hasPermission: () => false,
            checkPermission: () => false
        };
    }

    // 從 localStorage 獲取當前使用者的權限 ID 清單
    const getUserPermissionIds = () => {
        try {
            const permissionData = localStorage.getItem('userPermissions');
            if (!permissionData) {
                console.warn('未找到使用者權限資料');
                return [];
            }

            const permissions = JSON.parse(permissionData);

            // 檢查權限資料格式
            if (!permissions || !Array.isArray(permissions)) {
                console.error('權限資料格式錯誤，期望陣列格式:', permissions);
                return [];
            }

            return extractAllPermissionIds(permissions);
        } catch (error) {
            console.error('解析使用者權限資料失敗:', error);
            return [];
        }
    };

    // 遞迴提取所有權限 ID
    const extractAllPermissionIds = (permissions) => {
        const ids = [];

        const extract = (permissionList) => {
            // 檢查 permissionList 是否為陣列
            if (!Array.isArray(permissionList)) {
                console.error('權限資料不是陣列格式:', permissionList);
                return;
            }

            permissionList.forEach((permission) => {
                if (
                    permission &&
                    typeof permission === 'object' &&
                    permission.id
                ) {
                    ids.push(permission.id);
                    if (
                        permission.data &&
                        Array.isArray(permission.data) &&
                        permission.data.length > 0
                    ) {
                        extract(permission.data);
                    }
                }
            });
        };

        extract(permissions);
        return ids;
    };

    // 獲取頁面權限配置
    const pagePermissions = getPagePermissions(pageKey);

    // 檢查頁面權限配置是否存在
    if (!pagePermissions) {
        // 未找到頁面權限配置
    }

    // 使用者權限 ID 清單
    const userPermissionIds = computed(() => getUserPermissionIds());

    // 檢查是否有特定權限
    const hasPermission = (permissionId) => {
        try {
            if (
                !userPermissionIds.value ||
                !Array.isArray(userPermissionIds.value)
            ) {
                return false;
            }
            return userPermissionIds.value.includes(permissionId);
        } catch (error) {
            console.error('檢查權限時發生錯誤:', error);
            return false;
        }
    };

    // 檢查是否有頁面的查看權限
    const canView = computed(() => {
        if (!pagePermissions || !pagePermissions.view) return false;
        return hasPermission(pagePermissions.view);
    });

    // 檢查是否有頁面的新增權限
    const canCreate = computed(() => {
        if (!pagePermissions || !pagePermissions.create) return false;
        return hasPermission(pagePermissions.create);
    });

    // 檢查是否有頁面的修改權限
    const canUpdate = computed(() => {
        if (!pagePermissions || !pagePermissions.update) return false;
        return hasPermission(pagePermissions.update);
    });

    // 檢查是否有頁面的刪除權限
    const canDelete = computed(() => {
        if (!pagePermissions || !pagePermissions.delete) return false;
        return hasPermission(pagePermissions.delete);
    });

    // 檢查是否有頁面的複製權限
    const canCopy = computed(() => {
        if (!pagePermissions || !pagePermissions.copy) return false;
        return hasPermission(pagePermissions.copy);
    });

    // 檢查是否有頁面的產生權限
    const canGenerate = computed(() => {
        if (!pagePermissions || !pagePermissions.generate) return false;
        return hasPermission(pagePermissions.generate);
    });

    // 檢查是否有頁面的完成權限
    const canFinish = computed(() => {
        if (!pagePermissions || !pagePermissions.finish) return false;
        return hasPermission(pagePermissions.finish);
    });

    // 檢查是否有頁面的批量上傳權限
    const canBatchUpload = computed(() => {
        if (!pagePermissions || !pagePermissions.batchUpload) return false;
        return hasPermission(pagePermissions.batchUpload);
    });

    // 檢查是否有頁面的批量下載權限
    const canBatchDownload = computed(() => {
        if (!pagePermissions || !pagePermissions.batchDownload) return false;
        return hasPermission(pagePermissions.batchDownload);
    });

    // 檢查是否有頁面的匯出權限
    const canExport = computed(() => {
        if (!pagePermissions || !pagePermissions.export) return false;
        return hasPermission(pagePermissions.export);
    });

    // 檢查是否有頁面的角色分派權限
    const canAssignRole = computed(() => {
        if (!pagePermissions || !pagePermissions.assignRole) return false;
        return hasPermission(pagePermissions.assignRole);
    });

    // 檢查是否有任何操作權限（除了查看）
    const canOperate = computed(() => {
        return (
            canCreate.value ||
            canUpdate.value ||
            canDelete.value ||
            canCopy.value ||
            canGenerate.value ||
            canFinish.value ||
            canBatchUpload.value ||
            canBatchDownload.value ||
            canExport.value ||
            canAssignRole.value
        );
    });

    // 獲取頁面所有可用權限
    const availablePermissions = computed(() => {
        if (!pagePermissions) return {};

        return {
            view: canView.value,
            create: canCreate.value,
            update: canUpdate.value,
            delete: canDelete.value,
            copy: canCopy.value,
            generate: canGenerate.value,
            finish: canFinish.value,
            batchUpload: canBatchUpload.value,
            batchDownload: canBatchDownload.value,
            export: canExport.value,
            assignRole: canAssignRole.value
        };
    });

    // 通用權限檢查方法
    const checkPermission = (permissionType) => {
        if (!pagePermissions) return false;
        const permissionId = pagePermissions[permissionType];
        return permissionId ? hasPermission(permissionId) : false;
    };

    return {
        // 基本權限檢查
        canView,
        canCreate,
        canUpdate,
        canDelete,
        canCopy,
        canGenerate,
        canFinish,
        canBatchUpload,
        canBatchDownload,
        canExport,
        canAssignRole,
        canOperate,

        // 權限資料
        availablePermissions,
        userPermissionIds,

        // 通用方法
        hasPermission,
        checkPermission
    };
}

/**
 * 全域權限管理 composable
 * 提供全域權限相關功能
 */
export function useGlobalPermission() {
    // 更新使用者權限資料
    const updateUserPermissions = (permissions) => {
        try {
            localStorage.setItem(
                'userPermissions',
                JSON.stringify(permissions)
            );
        } catch (error) {
            console.error('儲存使用者權限資料失敗:', error);
        }
    };

    // 清除使用者權限資料
    const clearUserPermissions = () => {
        localStorage.removeItem('userPermissions');
    };

    // 獲取使用者權限資料
    const getUserPermissions = () => {
        try {
            const permissionData = localStorage.getItem('userPermissions');
            return permissionData ? JSON.parse(permissionData) : null;
        } catch (error) {
            console.error('獲取使用者權限資料失敗:', error);
            return null;
        }
    };

    // 檢查是否有任何權限
    const hasAnyPermission = () => {
        const permissions = getUserPermissions();
        return permissions && permissions.length > 0;
    };

    return {
        updateUserPermissions,
        clearUserPermissions,
        getUserPermissions,
        hasAnyPermission
    };
}
